import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { TeamDetailResponse, TeamInsightsResponse, TeamRead } from "@aura/shared";
import { api, type ApiResponse } from "~/lib/api";
import type { Period } from "~/lib/period";

/** Every teams query sits under "teams" so one invalidation refreshes them all. */
export const teamsKeys = {
  all: ["teams"] as const,
  insights: (range: Period, includeHidden = false) =>
    ["teams", "insights", { range, includeHidden }] as const,
  detail: (teamId: string, range: Period) => ["teams", "detail", teamId, { range }] as const,
  read: (range: Period, teamId: string | null) => ["teams", "read", teamId ?? "all", { range }] as const,
};

/**
 * `includeHidden` also lists hidden teams (flagged `hidden`), for admins
 * who may unhide them. Callers pass it only for admins.
 */
export function useTeamInsights(
  range: Period,
  opts: { enabled?: boolean; includeHidden?: boolean } = {},
) {
  const includeHidden = opts.includeHidden ?? false;
  return useQuery({
    enabled: opts.enabled ?? true,
    queryKey: teamsKeys.insights(range, includeHidden),
    queryFn: async () =>
      (
        await api.get<ApiResponse<TeamInsightsResponse>>(
          `/teams/insights?range=${range}${includeHidden ? "&includeHidden=1" : ""}`,
        )
      ).data,
    // Keep the page up while the admin toggles hidden teams on and off.
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

export function useTeamDetail(teamId: string | undefined, range: Period) {
  return useQuery({
    queryKey: teamsKeys.detail(teamId ?? "", range),
    queryFn: async () =>
      (
        await api.get<ApiResponse<TeamDetailResponse>>(
          `/teams/insights/${encodeURIComponent(teamId ?? "")}?range=${range}`,
        )
      ).data,
    enabled: !!teamId,
    staleTime: 60 * 1000,
  });
}

/**
 * The written read. Slow the first time in a period (the model searches the
 * web), so it loads on its own and never holds up the page.
 */
export function useTeamRead(range: Period, teamId: string | null = null) {
  return useQuery({
    queryKey: teamsKeys.read(range, teamId),
    queryFn: async () =>
      (
        await api.get<ApiResponse<TeamRead>>(
          `/teams/read?range=${range}${teamId ? `&teamId=${encodeURIComponent(teamId)}` : ""}`,
        )
      ).data,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
}
